import { ApolloError } from '@apollo/client';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { SnippetBox } from '@gamma/ui/display';
import { LoadingSpinner } from '@gamma/ui/progress';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router';
import dedent from 'ts-dedent';
import { i18n } from '../../../../../../shared/localization';
import { AuthContext } from '../../../../../../shared/providers/AuthProvider';
import {
  ChatGPTAnswer,
  ChatGPTPromptCloudPanel,
  ChatGPTQAPromptCloudPanel,
  ChatGPTToolTip,
} from '../../../../../entities/components';
import {
  IGetAlertMetadata,
  IGetDetections,
  useGetAlertMetadataNonSuricataAlertAttack,
  useGetAlertMetadataNonSuricataAlertTTP,
  useGetAlertMetadataSuricataAlertExplain,
  useGetAlertMetadataSuricataInvestigateAlert,
  useGetAlertMetadataSuricataRuleDescription,
} from '../../../../queries';

import { IGetDetectionVariables } from '../../../../components';
import { IDetectionContext } from '../../../Detection';

const {
  alerts,
  detection: detectionText,
  detectionSummary,
} = i18n.pages.detections;

const { chatGPT } = i18n.pages.integrations;

interface GPTPromptsProps {
  detailsProps?: {
    detectionData?: IGetDetections;
    detectionError?: ApolloError;
    detectionLoading?: boolean;
    metadataData?: IGetAlertMetadata;
    metadataError?: ApolloError;
    metadataLoading?: boolean;
    getDetectionVariables?: IGetDetectionVariables;
  };
  isNextSteps?: boolean;
  horizontal?: boolean;
  hideNextSteps?: boolean;
  hideDescription?: boolean;
}

export const GPTPrompts = ({
  detailsProps,
  isNextSteps,
  horizontal = false,
  hideNextSteps = false,
  hideDescription = false,
}: GPTPromptsProps) => {
  const {
    detectionData,
    detectionError,
    detectionLoading,
    metadataData,
    metadataError,
    metadataLoading,
    getDetectionVariables,
  } = useOutletContext<IDetectionContext>() || {};

  const detectionDetailsProps = useMemo(() => {
    return detailsProps
      ? {
          ...detailsProps,
        }
      : {
          detectionData,
          detectionError,
          detectionLoading,
          metadataData,
          metadataError,
          metadataLoading,
          getDetectionVariables,
        };
  }, [
    detailsProps,
    detectionData,
    detectionError,
    detectionLoading,
    metadataData,
    metadataError,
    metadataLoading,
    getDetectionVariables,
  ]);

  const { userPrivileges, isOrgTenant, loadPrivilegesForTenant } =
    useContext(AuthContext);

  const [descriptionText, setDescriptionText] = useState<string>('');
  const [chatGPTAnswersList, setChatGPTAnswersList] = useState<ChatGPTAnswer[]>(
    [],
  );
  const [chatGPTQuestionsList, setChatGPTQuestionsList] = useState<string[]>(
    [],
  );
  const [suricataRuleText, setSuricataRuleText] = useState('');

  const detectionInfo =
    detectionDetailsProps?.detectionData?.getDetections?.detections?.[0];

  const { alert_info } = detectionInfo || {};
  const activeTenantId = detectionInfo?.tenant_info?.tenant_id ?? '';

  // tri-state for privilege: true | false | null (unknown yet)
  const rawGPT = userPrivileges?.[activeTenantId]?.chatgpt;
  const isGPTEnabled: boolean | null =
    rawGPT === undefined ? null : Boolean(rawGPT);

  const GPTQuestionList = Object.values(
    alert_info?.alert_type === 'suricata_corelight'
      ? chatGPT.suricataQuestions
      : chatGPT.nonSuricataQuestions,
  );

  useEffect(() => {
    if (alert_info?.alert_type) {
      setChatGPTQuestionsList(GPTQuestionList);
    }
  }, [alert_info?.alert_type]);

  const { description } =
    detectionDetailsProps?.metadataData?.getAlertMetadata?.metadata?.[0] || {};

  const gptQueryVariables = {
    items: [
      {
        content_id: detectionInfo?.alert_info?.content_id,
        tenant: detectionInfo?.tenant_info?.tenant_id,
      },
    ],
  };

  // suri_alert_explain
  const [
    getSuricataAlertExplain,
    {
      loading: getSuricataAlertExplainLoading,
      data: getSuricataAlertExplainData,
      error: getSuricataAlertExplainError,
    },
  ] = useGetAlertMetadataSuricataAlertExplain({
    variables: gptQueryVariables,
  });

  // suri_investigate_alert
  const [
    getSuricataAlertInvestigation,
    {
      loading: getSuricataAlertInvestigationLoading,
      data: getSuricataAlertInvestigationData,
      error: getSuricataAlertInvestigationError,
    },
  ] = useGetAlertMetadataSuricataInvestigateAlert({
    variables: gptQueryVariables,
  });

  // non_suri_alert_attack
  const [
    getNonSuricataAlertAttack,
    {
      loading: getNonSuricataAlertAttackLoading,
      data: getNonSuricataAlertAttackData,
      error: getNonSuricataAlertAttackError,
    },
  ] = useGetAlertMetadataNonSuricataAlertAttack({
    variables: gptQueryVariables,
  });

  // non_suri_alert_ttp
  const [
    getNonSuricataAlertTTP,
    {
      loading: getNonSuricataAlertTTPLoading,
      data: getNonSuricataAlertTTPData,
      error: getNonSuricataAlertTTPError,
    },
  ] = useGetAlertMetadataNonSuricataAlertTTP({
    variables: gptQueryVariables,
  });

  const isLoadingGPTQuestionPromptData =
    getSuricataAlertExplainLoading ||
    getSuricataAlertInvestigationLoading ||
    getNonSuricataAlertAttackLoading ||
    getNonSuricataAlertTTPLoading;

  const hasGPTQuestionPromptError =
    getSuricataAlertExplainError ||
    getSuricataAlertInvestigationError ||
    getNonSuricataAlertAttackError ||
    getNonSuricataAlertTTPError;

  const chatGPTQuestionPromptData = useMemo(() => {
    return {
      suricataAlertExplain:
        getSuricataAlertExplainData?.getAlertMetadata?.metadata?.[0]
          ?.chatbot_chats?.suri_alert_explain,
      suricataAlertInvestigation:
        getSuricataAlertInvestigationData?.getAlertMetadata?.metadata?.[0]
          ?.chatbot_chats?.suri_investigate_alert,
      nonSuricataAlertAttack:
        getNonSuricataAlertAttackData?.getAlertMetadata?.metadata?.[0]
          ?.chatbot_chats?.non_suri_alert_attack,
      nonSuricataAlertTTP:
        getNonSuricataAlertTTPData?.getAlertMetadata?.metadata?.[0]
          ?.chatbot_chats?.non_suri_alert_ttp,
    };
  }, [
    getSuricataAlertExplainData,
    getSuricataAlertInvestigationData,
    getNonSuricataAlertAttackData,
    getNonSuricataAlertTTPData,
  ]);

  const callChatGPTPromptDataFunction = (question: string) => {
    const questionToAnswerMap = {
      [`${chatGPT.suricataQuestions.whatDoesThisAlertMean.toLowerCase()}`]:
        getSuricataAlertExplain,
      [`${chatGPT.suricataQuestions.whatNextStepShouldITake.toLowerCase()}`]:
        getSuricataAlertInvestigation,
      [`${chatGPT.nonSuricataQuestions.whatTypeOfAttacksMayBeAssociated.toLowerCase()}`]:
        getNonSuricataAlertAttack,
      [`${chatGPT.nonSuricataQuestions.whatAreSomeTechniquesSpecificToThisAlert.toLowerCase()}`]:
        getNonSuricataAlertTTP,
    };

    const gptFunction = questionToAnswerMap[question.toLowerCase()];

    if (typeof gptFunction === 'function') {
      gptFunction();
    }
  };

  // ORG view: if we don't yet have privileges for this member tenant, load them
  useEffect(() => {
    const tid = activeTenantId;
    if (
      isOrgTenant &&
      tid &&
      userPrivileges?.[tid] === undefined &&
      loadPrivilegesForTenant
    ) {
      loadPrivilegesForTenant(tid).catch((e) =>
        console.warn(
          '[Detections] failed to load member privileges for',
          tid,
          e,
        ),
      );
    }
  }, [isOrgTenant, activeTenantId, userPrivileges, loadPrivilegesForTenant]);

  // update answer list based on result of lazilyDynamicallyLoadGetAlertMetadata
  useEffect(() => {
    const whatDoesThisAlertMean =
      chatGPTQuestionPromptData?.suricataAlertExplain;
    const nextStepsAnswerText =
      chatGPTQuestionPromptData?.suricataAlertInvestigation;
    const associatedAttacksText =
      chatGPTQuestionPromptData?.nonSuricataAlertAttack;
    const ttpAdversariesText = chatGPTQuestionPromptData?.nonSuricataAlertTTP;

    const hasWhatDoesThisAlertMean = chatGPTAnswersList.some(
      (answer) =>
        answer.questionText.toLowerCase() ===
        chatGPT.suricataQuestions.whatDoesThisAlertMean.toLowerCase(),
    );

    const hasNextStepsInAnswersList = chatGPTAnswersList.some(
      (answer) =>
        answer.questionText.toLowerCase() ===
        chatGPT.suricataQuestions.whatNextStepShouldITake.toLowerCase(),
    );

    const hasAssociatedAttacksInAnswersList = chatGPTAnswersList.some(
      (answer) =>
        answer.questionText.toLowerCase() ===
        chatGPT.nonSuricataQuestions.whatTypeOfAttacksMayBeAssociated.toLowerCase(),
    );

    const hasTTPAdversariesInAnswersList = chatGPTAnswersList.some(
      (answer) =>
        answer.questionText.toLowerCase() ===
        chatGPT.nonSuricataQuestions.whatAreSomeTechniquesSpecificToThisAlert.toLowerCase(),
    );

    if (whatDoesThisAlertMean && !hasWhatDoesThisAlertMean) {
      setChatGPTAnswersList((prev) => [
        ...prev,
        {
          questionText: chatGPT.suricataQuestions.whatDoesThisAlertMean,
          answerText: whatDoesThisAlertMean,
        },
      ]);
    }

    if (nextStepsAnswerText && !hasNextStepsInAnswersList) {
      setChatGPTAnswersList((prev) => [
        ...prev,
        {
          questionText: chatGPT.suricataQuestions.whatNextStepShouldITake,
          answerText: nextStepsAnswerText,
        },
      ]);
    }

    if (associatedAttacksText && !hasAssociatedAttacksInAnswersList) {
      setChatGPTAnswersList((prev) => [
        ...prev,
        {
          questionText:
            chatGPT.nonSuricataQuestions.whatTypeOfAttacksMayBeAssociated,
          answerText: associatedAttacksText,
        },
      ]);
    }

    if (ttpAdversariesText && !hasTTPAdversariesInAnswersList) {
      setChatGPTAnswersList((prev) => [
        ...prev,
        {
          questionText:
            chatGPT.nonSuricataQuestions
              .whatAreSomeTechniquesSpecificToThisAlert,
          answerText: ttpAdversariesText,
        },
      ]);
    }
  }, [chatGPTQuestionPromptData]);

  const hasChatGPTQuestionPromptData = Boolean(
    (chatGPTQuestionPromptData?.suricataAlertExplain ||
      chatGPTQuestionPromptData?.suricataAlertInvestigation ||
      chatGPTQuestionPromptData?.nonSuricataAlertAttack ||
      chatGPTQuestionPromptData?.nonSuricataAlertTTP) &&
      descriptionText?.length > 0,
  );

  const {
    data: suricataAlertDescriptionData,
    loading: suricataAlertDescriptionLoading,
    error: suricataAlertDescriptionError,
  } = useGetAlertMetadataSuricataRuleDescription({
    skip:
      isGPTEnabled !== true ||
      !detectionInfo?.alert_info?.content_id ||
      detectionDetailsProps?.metadataLoading ||
      alert_info?.alert_type !== 'suricata_corelight' ||
      hasChatGPTQuestionPromptData,
    variables: gptQueryVariables,
  });

  const [currentQuestionPromptText, setCurrentQuestionPromptText] =
    useState<string>('');

  const handleUpdatingChatGPTQuestionsAndAnswersList = async (
    question: string,
    hasError: boolean,
  ) => {
    setCurrentQuestionPromptText(question);

    callChatGPTPromptDataFunction(question);

    const isQuestionInList = chatGPTQuestionsList.includes(question);

    if (isQuestionInList && !hasError) {
      const newQuestionsList = chatGPTQuestionsList.filter(
        (q) => q !== question,
      );
      setChatGPTQuestionsList(newQuestionsList);
    } else {
      setChatGPTQuestionsList([...chatGPTQuestionsList, question]);
    }

    if (hasError) {
      setChatGPTQuestionsList(GPTQuestionList);
    }
  };

  // set panel description
  useEffect(() => {
    if (detectionDetailsProps?.metadataLoading) {
      return;
    }

    if (
      alert_info?.alert_type === 'suricata_corelight' &&
      !descriptionText?.length
    ) {
      setDescriptionText(
        suricataAlertDescriptionData?.getAlertMetadata?.metadata?.[0]
          ?.chatbot_chats?.suri_describe_rule ?? '',
      );
    }

    if (
      description &&
      alert_info?.alert_type !== 'suricata_corelight' &&
      alert_info?.alert_type &&
      !descriptionText?.length
    ) {
      setDescriptionText(description);
    }

    const srt =
      ((alert_info?.alert_type === 'suricata_corelight' &&
        suricataAlertDescriptionData?.getAlertMetadata?.metadata?.[0]?.rule) ||
        detectionDetailsProps?.metadataData?.getAlertMetadata?.metadata?.[0]
          ?.rule) ??
      '';

    setSuricataRuleText(srt);
  }, [
    suricataAlertDescriptionData,
    description,
    detectionDetailsProps?.metadataLoading,
    alert_info?.alert_type,
    detectionDetailsProps?.metadataData?.getAlertMetadata?.metadata,
    descriptionText?.length,
  ]);

  const filteredQuestions = useMemo(() => {
    const nextStepQuestion = chatGPT.suricataQuestions.whatNextStepShouldITake;

    let questions = [...chatGPTQuestionsList];

    if (isNextSteps) {
      questions = questions.includes(nextStepQuestion)
        ? [nextStepQuestion]
        : [];
    }

    if (hideNextSteps) {
      questions = questions.filter(
        (q) => q.toLowerCase() !== nextStepQuestion.toLowerCase(),
      );
    }

    return questions;
  }, [isNextSteps, chatGPTQuestionsList, hideNextSteps]);

  const Wrapper = horizontal ? HStack : VStack;

  const descriptionTextDisplay =
    descriptionText ===
    "Please provide the Suricata rule you would like me to summarize, and I'll be happy to help!"
      ? detectionSummary.noDescriptionText
      : descriptionText;

  return (
    <VStack w="100%" alignItems="start">
      <Wrapper w="100%" alignItems="start">
        {alert_info?.alert_type === 'suricata_corelight' && (
          <Wrapper w="100%" alignItems="start">
            {!isNextSteps &&
              (horizontal ? (
                <VStack flex={1} alignItems="start">
                  <HStack alignItems="start">
                    <ChatGPTToolTip boxSize={4} />
                    <Text color="text.secondary">
                      {detectionSummary.ruleDescriptionLabel}
                    </Text>
                  </HStack>
                  <ChatGPTPromptCloudPanel
                    titleText={chatGPT.description}
                    descriptionText={descriptionTextDisplay}
                    isLoadingDescription={
                      suricataAlertDescriptionLoading ||
                      detectionDetailsProps?.metadataLoading
                    }
                    isDescriptionQuickView={false}
                    loadingDescriptionError={suricataAlertDescriptionError}
                    corelightDescription={description ?? undefined}
                    storageKey="gpt-prompt-panel-suri-details"
                    showGPTDisabled={true}
                    // must be boolean; coerce tri-state
                    isGPTEnabled={isGPTEnabled !== false}
                    horizontal={horizontal}
                  />
                </VStack>
              ) : (
                <ChatGPTPromptCloudPanel
                  titleText={chatGPT.description}
                  descriptionText={descriptionTextDisplay}
                  isLoadingDescription={
                    suricataAlertDescriptionLoading ||
                    detectionDetailsProps?.metadataLoading
                  }
                  isDescriptionQuickView={false}
                  loadingDescriptionError={suricataAlertDescriptionError}
                  corelightDescription={description ?? undefined}
                  storageKey="gpt-prompt-panel-suri-details"
                  showGPTDisabled={false}
                  // must be boolean; coerce tri-state
                  isGPTEnabled={isGPTEnabled !== false}
                  horizontal={horizontal}
                />
              ))}
            {!isNextSteps && detectionDetailsProps?.metadataLoading ? (
              <LoadingSpinner />
            ) : !isNextSteps && suricataRuleText ? (
              <VStack
                w={horizontal ? '50%' : '100%'}
                maxW={horizontal ? '50%' : '100%'}
                alignItems="start"
              >
                <Text color={horizontal ? 'text.secondary' : 'text.primary'}>
                  {alerts.suricata.title} {detectionText?.rule}
                </Text>
                <Box w="100%" maxW="100%" overflowX="auto" overflow="hidden">
                  <SnippetBox
                    snippet={dedent`${suricataRuleText}`}
                    isCopyable={true}
                  />
                </Box>
              </VStack>
            ) : null}
            {!horizontal && (
              <ChatGPTQAPromptCloudPanel
                titleText={!isNextSteps ? chatGPT.askGPT : ''}
                descriptionText={!isNextSteps ? descriptionTextDisplay : ''}
                questionsTextList={filteredQuestions}
                answersTextList={chatGPTAnswersList}
                isLoadingAnswerList={isLoadingGPTQuestionPromptData}
                questionLoadingText={currentQuestionPromptText}
                onQuestionClick={handleUpdatingChatGPTQuestionsAndAnswersList}
                loadingAnswerListError={hasGPTQuestionPromptError}
                storageKey="gpt-prompt-panel-suri-ask-details"
                isGPTEnabled={isGPTEnabled !== false}
                isNextSteps={isNextSteps}
                horizontal={horizontal}
              />
            )}
          </Wrapper>
        )}
        {!isNextSteps &&
          !hideDescription &&
          alert_info?.alert_type !== 'suricata_corelight' &&
          !description && (
            <ChatGPTPromptCloudPanel
              titleText={chatGPT.description}
              descriptionText={descriptionText}
              isLoadingDescription={
                !descriptionText && suricataAlertDescriptionLoading
              }
              isDescriptionQuickView={false}
              loadingDescriptionError={suricataAlertDescriptionError}
              storageKey="gpt-prompt-panel-details"
              showGPTDisabled={true}
              isGPTEnabled={isGPTEnabled !== false}
            />
          )}
        {!isNextSteps &&
          !hideDescription &&
          alert_info?.alert_type !== 'suricata_corelight' &&
          description && (
            <ChatGPTQAPromptCloudPanel
              titleText={chatGPT.askGPT}
              descriptionText={descriptionTextDisplay}
              questionsTextList={chatGPTQuestionsList}
              answersTextList={chatGPTAnswersList}
              isLoadingAnswerList={isLoadingGPTQuestionPromptData}
              questionLoadingText={currentQuestionPromptText}
              onQuestionClick={handleUpdatingChatGPTQuestionsAndAnswersList}
              loadingAnswerListError={hasGPTQuestionPromptError}
              isLoadingDescription={
                !descriptionText && suricataAlertDescriptionLoading
              }
              storageKey="gpt-prompt-non-suri-search-panel-details"
              isGPTEnabled={isGPTEnabled !== false}
            />
          )}
      </Wrapper>
      {horizontal && (
        <ChatGPTQAPromptCloudPanel
          titleText={!isNextSteps ? chatGPT.askGPT : ''}
          descriptionText={!isNextSteps ? descriptionTextDisplay : ''}
          questionsTextList={filteredQuestions}
          answersTextList={chatGPTAnswersList}
          isLoadingAnswerList={isLoadingGPTQuestionPromptData}
          questionLoadingText={currentQuestionPromptText}
          onQuestionClick={handleUpdatingChatGPTQuestionsAndAnswersList}
          loadingAnswerListError={hasGPTQuestionPromptError}
          storageKey="gpt-prompt-panel-suri-ask-details"
          isGPTEnabled={isGPTEnabled !== false}
          isNextSteps={isNextSteps}
          horizontal={horizontal}
        />
      )}
    </VStack>
  );
};
